import { QueryClient, ResetOptions, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { resetTellescopeQueries } from "./reset-tellescope-queries";

/**
 * Arguments for resetting all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface ResetTellescopeModelQueriesArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to reset queries for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TError,
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TellescopeQueryKey<Model, QueryMethodForModel<Model>>
    >,
    "queryKey"
  >;
  /** Optional reset options (e.g., throwOnError) applied to all resets */
  options?: ResetOptions;
  /**
   * Optional method-specific filters. Allows resetting queries for specific methods
   * and optionally matching by request parameters.
   */
  methodFilters?: Partial<{
    [Method in QueryMethodForModel<Model>]: Omit<
      TellescopeQueryFilters<
        QueryDataForMethod<Model, Method>,
        TError,
        QueryDataForMethod<Model, Method>,
        TellescopeQueryKey<Model, Method>
      >,
      "queryKey"
    > & {
      /** Optional request parameters to match for this method */
      request?: Partial<ParamsForMethod<Model, Method>>;
      /** Optional method-specific reset options that override global options */
      options?: ResetOptions;
    };
  }>;
}

/**
 * Resets all cached queries for a specific Tellescope model.
 *
 * This function provides a way to reset all queries for a model, optionally
 * with method-specific filters. When no methodFilters are provided, it resets
 * all queries for the model. When methodFilters are provided, it only resets
 * queries for the specified methods.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, and optional filters
 * @returns A promise that resolves when reset is complete
 *
 * @example
 * ```ts
 * // Reset all user queries
 * await resetTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 * });
 * ```
 *
 * @example
 * ```ts
 * // Reset only specific methods
 * await resetTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   methodFilters: {
 *     getOne: {
 *       // Reset all getOne queries
 *     },
 *     getSome: {
 *       request: { filter: { fname: "John" }, limit: 10 },
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global filters with method-specific filters
 * await resetTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   filters: {
 *     stale: true, // Only reset stale queries
 *   },
 *   methodFilters: {
 *     getOne: {
 *       // Inherits stale: true from global filters
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global and method-specific options
 * await resetTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   options: {
 *     throwOnError: true, // Global option for all methods
 *   },
 *   methodFilters: {
 *     getOne: {
 *       options: {
 *         throwOnError: false, // Method-specific option (overrides global)
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const resetTellescopeModelQueries = async <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  filters,
  methodFilters,
  options,
}: ResetTellescopeModelQueriesArgs<Model, TError>) => {
  const methodNames = Object.keys(methodFilters ?? {}) as QueryMethodForModel<Model>[];

  // If no method filters are specified, reset all queries for the model
  if (!methodFilters || !methodNames.length) {
    return queryClient.resetQueries(
      {
        queryKey: [model],
        exact: false,
        ...filters,
      } as QueryFilters<Model[]>,
      options
    );
  }

  // Otherwise, reset queries for each specified method
  const resetPromises = methodNames.map(<Method extends QueryMethodForModel<Model>>(method: Method) => {
    const { request, options: methodOptions, ...methodSpecificFilters } = methodFilters[method] ?? {};

    return resetTellescopeQueries<Model, Method, TError>({
      model,
      method,
      request,
      queryClient,
      filters: {
        ...filters,
        ...methodSpecificFilters,
      },
      options: { ...options, ...methodOptions },
    });
  });

  await Promise.all(resetPromises);
};
